import { Component, inject, signal, effect, input, computed, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SplitFlapBoard } from '../../components/split-flap-board/split-flap-board';
import { BoardApiService } from '../../services/board-api.service';
import { AuthService } from '../../services/auth.service';
import { Board } from '../../models/board.model';
import { catchError, of, Subscription } from 'rxjs';

/**
 * Board display page
 *
 * Scenarios:
 * 1. Preview from dashboard: Auth already loaded → Load with JWT
 * 2. Direct link in new tab: Auth loads → Check if authenticated → Load with JWT or show passcode
 * 3. Shared link: Not authenticated → Show passcode form
 */
@Component({
  selector: 'app-board-display',
  standalone: true,
  imports: [SplitFlapBoard, FormsModule],
  templateUrl: './board-display.html',
  styleUrl: './board-display.css',
})
export class BoardDisplay implements OnDestroy {
  private refreshTimer?: ReturnType<typeof setInterval>;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private initialRequest?: Subscription;
  private refreshRequest?: Subscription;
  private heartbeatRequest?: Subscription;
  private currentMessageId: string | null = null;
  private acceptedPasscode?: string;
  private destroyed = false;
  private auth = inject(AuthService);
  private boardApi = inject(BoardApiService);

  // Route parameter as input (can be either internal ID or public_id)
  id = input.required<string>();

  // Computed: is this a public_id (6 chars, alphanumeric) or internal ID (numeric)?
  private readonly isPublicId = computed(() => {
    const idValue = this.id();
    // Public IDs are exactly 6 characters and alphanumeric
    // Internal IDs are numeric (potentially many digits)
    return idValue.length === 6 && /^[a-zA-Z0-9]{6}$/.test(idValue);
  });

  // Auth state
  protected readonly isAuthenticated = this.auth.isAuthenticated;
  protected readonly authLoading = this.auth.isLoading;

  // Component state
  protected readonly board = signal<Board | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly showPasscodeForm = signal(false);
  protected readonly passcode = signal('');
  protected readonly passcodeError = signal<string | null>(null);

  private hasInitialized = signal(false);

  constructor() {
    // Wait for the auth session check to finish, then decide how to load the board
    effect(() => {
      const authLoading = this.authLoading();
      const authenticated = this.isAuthenticated();

      if (!authLoading && !this.hasInitialized()) {
        this.hasInitialized.set(true);

        this.loadBoard();
      }
    });
  }

  /**
   * Load board from API
   * - If ID is numeric (internal ID) and user is authenticated: Use /api/app/boards/:id (with JWT)
   * - If ID is 6-char alphanumeric (public_id): Use /api/display/:publicId (with optional passcode)
   */
  private loadBoard(passcode?: string): void {
    this.isLoading.set(true);
    this.error.set(null);
    if (passcode) {
      this.passcodeError.set(null);
    }

    const idValue = this.id();
    const isAuthenticated = this.isAuthenticated();
    const isPublic = this.isPublicId();

    // Choose the appropriate endpoint:
    // 1. Internal ID + authenticated → use authenticated endpoint (board owner preview)
    // 2. Public ID (regardless of auth) → use public endpoint (public sharing)
    const request$ =
      !isPublic && isAuthenticated
        ? this.boardApi.getBoard(idValue) // Authenticated endpoint (uses internal ID with JWT)
        : this.boardApi.getPublicBoard(idValue, passcode); // Public endpoint (uses public_id with passcode)

    this.initialRequest = request$
      .pipe(catchError((err) => this.handleLoadError(err, !!passcode, isAuthenticated)))
      .subscribe((board) => {
        if (this.destroyed) return;
        if (board) {
          this.board.set(board);
          this.acceptedPasscode = passcode;
          this.showPasscodeForm.set(false);
          this.startObservation();
        }
        this.isLoading.set(false);
      });
  }

  private startObservation(): void {
    clearInterval(this.refreshTimer);
    clearInterval(this.heartbeatTimer);
    this.refreshTimer = setInterval(() => this.refreshBoard(), 3000);
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), 4000);
  }

  protected onDisplayMessage(id: string | null): void {
    this.currentMessageId = id;
    this.sendHeartbeat();
  }

  private sendHeartbeat(): void {
    const board = this.board();
    if (!board || this.destroyed || (this.heartbeatRequest && !this.heartbeatRequest.closed))
      return;
    const request = this.isPublicId()
      ? this.boardApi.reportDisplay(board.public_id, this.currentMessageId, this.acceptedPasscode)
      : this.boardApi.reportOwnerDisplay(board.id, this.currentMessageId);
    this.heartbeatRequest = request.subscribe({ error: () => {} });
  }

  private refreshBoard(): void {
    if (this.refreshRequest && !this.refreshRequest.closed) return;
    const request = this.isPublicId()
      ? this.boardApi.getPublicBoard(this.id(), this.acceptedPasscode)
      : this.boardApi.getBoard(this.id());
    this.refreshRequest = request.subscribe({
      next: (incoming) => {
        if (this.destroyed) return;
        const current = this.board();
        if (!current) return;
        const config =
          JSON.stringify(current.config) === JSON.stringify(incoming.config)
            ? current.config
            : incoming.config;
        const messages =
          JSON.stringify(current.messages) === JSON.stringify(incoming.messages)
            ? current.messages
            : incoming.messages;
        // Preserve references for unchanged rotation inputs: polling must not rearm a cycle.
        this.board.set({ ...incoming, config, messages });
      },
      error: (error) => {
        if ([401, 403, 404].includes(error.status)) {
          this.board.set(null);
          clearInterval(this.heartbeatTimer);
          clearInterval(this.refreshTimer);
          this.handleLoadError(error, !!this.acceptedPasscode, this.isAuthenticated());
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearInterval(this.refreshTimer);
    clearInterval(this.heartbeatTimer);
    this.initialRequest?.unsubscribe();
    this.refreshRequest?.unsubscribe();
    this.heartbeatRequest?.unsubscribe();
  }

  /**
   * Handle board loading errors
   */
  private handleLoadError(err: any, hasPasscode: boolean, isAuthenticated: boolean) {
    this.isLoading.set(false);

    if (err.status === 404) {
      if (isAuthenticated) {
        // Authenticated user doesn't own this board
        this.error.set("Board not found or you don't have access to it");
      } else {
        // Board doesn't exist
        this.error.set('Board not found');
      }
      this.showPasscodeForm.set(false);
    } else if (err.status === 401) {
      if (hasPasscode) {
        // Invalid passcode
        this.showPasscodeForm.set(true);
        this.passcodeError.set('Incorrect passcode. Please try again.');
      } else if (isAuthenticated && !this.isPublicId()) {
        // Invalid or expired JWT on authenticated (owner) endpoint
        this.error.set('Authentication expired. Please log in again.');
      } else {
        // No passcode provided when required (public endpoint, any auth state)
        this.showPasscodeForm.set(true);
      }
    } else {
      // Other errors
      this.error.set(err.error?.message || 'Failed to load board');
      this.showPasscodeForm.set(false);
    }

    return of(null);
  }

  /**
   * Submit passcode form
   */
  protected submitPasscode(): void {
    const passcodeValue = this.passcode().trim();

    if (!passcodeValue) {
      this.passcodeError.set('Please enter a passcode');
      return;
    }

    this.loadBoard(passcodeValue);
  }
}
